import React from "react";
import { Link } from "react-router-dom";
import AppShell from "../layout/AppShell";
import StatCard from "../components/ui/StatCard";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  MdPeople,
  MdSchool,
  MdForum,
  MdInsights,
  MdOpenInNew,
} from "react-icons/md";
import {
  getAppStats,
  getCertsStats,
  type Range,
} from "../services/statsService";
import {
  getFirestore,
  collection,
  getDocs,
  orderBy,
  limit,
  query as fsQuery,
  type Timestamp,
} from "firebase/firestore";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

type Stats = {
  users: number | null;
  certifications: number | null;
  posts: number | null;
};

type RecentPost = {
  id: string;
  text: string;
  createdAt?: Date | null;
  like?: number;
  recommend?: number;
  comment?: number;
};

function readKpi(obj: unknown, keys: readonly string[]): number | null {
  if (!obj || typeof obj !== "object") return null;
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

const toDate = (v: any): Date | null =>
  v?.toDate?.() instanceof Date
    ? (v as Timestamp).toDate()
    : v instanceof Date
    ? v
    : null;

const fmt = (d?: Date | null) =>
  d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

/** Barra de progreso simple (MD3-ish) */
function ProgressBar({
  value,
  total,
  label,
}: {
  value: number;
  total: number;
  label: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="opacity-80">{label}</span>
        <span className="tabular-nums">
          {value} · {pct}%
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface/60">
        <div
          className="h-2 rounded-full bg-primary"
          style={{ width: `${pct}%` }}
          aria-label={`${label} ${pct}%`}
        />
      </div>
    </div>
  );
}

export default function DashboardHome(): React.ReactElement {
  const [stats, setStats] = React.useState<Stats>({
    users: null,
    certifications: null,
    posts: null,
  });

  const [trend, setTrend] = React.useState<
    Array<{ date: string; posts: number }>
  >([]);
  const [recent, setRecent] = React.useState<RecentPost[]>([]);
  const [certBars, setCertBars] = React.useState<{
    total: number;
    official: number;
    trial: number;
    final: number;
    draft: number;
  }>({
    total: 0,
    official: 0,
    trial: 0,
    final: 0,
    draft: 0,
  });

  // 🔄 spinners
  const [loadingMain, setLoadingMain] = React.useState<boolean>(true);
  const [loadingRecent, setLoadingRecent] = React.useState<boolean>(true);

  React.useEffect(() => {
    let alive = true;

    const range: Range = { kind: "lastDays", days: 30 };

    (async () => {
      try {
        setLoadingMain(true);

        // KPIs + serie diaria (para sparkline)
        const { kpis, daily } = await getAppStats(range);

        const users = readKpi(kpis, ["usersTotal", "users", "totalUsers"]);
        const certifications = readKpi(kpis, [
          "certificationsTotal",
          "certifications",
          "totalCertifications",
        ]);
        const posts = readKpi(kpis, ["postsTotal", "posts", "totalPosts"]);

        // Tendencia últimos 14 días
        const last14 = daily
          .slice(-14)
          .map((d) => ({ date: d.date.slice(5), posts: d.posts }));

        // Resumen certificaciones (all-time)
        const certs = await getCertsStats();
        const certData = {
          total: certs.total,
          official: certs.official,
          trial: certs.trial,
          final: certs.final,
          draft: certs.draft,
        };

        if (!alive) return;
        setStats({ users, certifications, posts });
        setTrend(last14);
        setCertBars(certData);
      } catch {
        if (alive) {
          setStats({ users: null, certifications: null, posts: null });
          setTrend([]);
          setCertBars({ total: 0, official: 0, trial: 0, final: 0, draft: 0 });
        }
      } finally {
        if (alive) setLoadingMain(false);
      }
    })();

    // Actividad reciente (5 posts)
    (async () => {
      try {
        setLoadingRecent(true);
        const db = getFirestore();
        const snap = await getDocs(
          fsQuery(
            collection(db, "communityPosts"),
            orderBy("createdAt", "desc"),
            limit(5)
          )
        );
        const items: RecentPost[] = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            text: typeof data.text === "string" ? data.text : "(sin texto)",
            createdAt: toDate(data.createdAt),
            like: Number(data?.counters?.like ?? 0) || 0,
            recommend: Number(data?.counters?.recommend ?? 0) || 0,
            comment: Number(data?.counters?.comment ?? 0) || 0,
          };
        });
        if (!alive) return;
        setRecent(items);
      } catch {
        if (alive) setRecent([]);
      } finally {
        if (alive) setLoadingRecent(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      {/* KPIs */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          title="Usuarios"
          value={stats.users}
          icon={<MdPeople />}
          className="transition hover:shadow-md"
        />
        <StatCard
          title="Certificaciones"
          value={stats.certifications}
          icon={<MdSchool />}
          className="transition hover:shadow-md"
        />
        <StatCard
          title="Posts comunidad"
          value={stats.posts}
          icon={<MdForum />}
          className="transition hover:shadow-md"
        />
      </section>

      {/* Trend + Recent + Cert Bars + Shortcuts */}
      <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Tendencia */}
        <div className="card p-4 xl:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <MdInsights />
            </div>
            <h3 className="text-lg font-semibold">
              Tendencia 14 días — Comunidad
            </h3>
          </div>

          <div className="h-56">
            {loadingMain ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeOpacity={0.1} />
                  <XAxis dataKey="date" tickMargin={8} />
                  <YAxis width={28} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="posts"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="mt-2 text-sm opacity-70">
            Posts por día (últimos 14 días)
          </div>
        </div>

        {/* Certificaciones — resumen */}
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <MdSchool />
            </div>
            <h3 className="text-lg font-semibold">Certificaciones — Resumen</h3>
          </div>

          {loadingMain ? (
            <div className="flex h-40 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-2 text-sm opacity-70">Totales (histórico)</div>
              <ProgressBar
                label="Oficiales"
                value={certBars.official}
                total={certBars.total || 1}
              />
              <ProgressBar
                label="De prueba"
                value={certBars.trial}
                total={certBars.total || 1}
              />
              <div className="mt-4 mb-2 text-sm opacity-70">Por estado</div>
              <ProgressBar
                label="Finalizadas"
                value={certBars.final}
                total={certBars.final + certBars.draft || 1}
              />
              <ProgressBar
                label="Draft"
                value={certBars.draft}
                total={certBars.final + certBars.draft || 1}
              />
              <div className="mt-3 text-sm opacity-70">
                Total:{" "}
                <span className="font-medium tabular-nums">
                  {certBars.total}
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Actividad reciente */}
        <div className="card p-4 xl:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <MdForum />
              </div>
              <h3 className="text-lg font-semibold">
                Actividad reciente — Comunidad
              </h3>
            </div>
            <Link className="btn btn-outline" to="/community">
              Ver comunidad <MdOpenInNew />
            </Link>
          </div>

          {loadingRecent ? (
            <div className="flex h-40 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : recent.length === 0 ? (
            <div className="text-sm opacity-70">
              No hay publicaciones recientes.
            </div>
          ) : (
            <ul className="divide-y divide-surface/80">
              {recent.map((p) => (
                <li key={p.id} className="py-3">
                  <div className="mb-1 line-clamp-2">{p.text}</div>
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-70">
                    <span>{fmt(p.createdAt)}</span>
                    <span>👍 {p.like}</span>
                    <span>⭐ {p.recommend}</span>
                    <span>💬 {p.comment}</span>
                    <Link
                      className="text-primary underline"
                      to={`/community/${p.id}`}
                    >
                      Abrir
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Atajos */}
        <div className="card p-4">
          <h3 className="mb-3 text-lg font-semibold">Atajos</h3>
          <div className="grid grid-cols-1 gap-2">
            <Link to="/community" className="btn btn-primary w-full">
              Ir a Comunidad
            </Link>
            <Link to="/certifications" className="btn btn-outline w-full">
              Ver Certificaciones
            </Link>
            <Link to="/stats" className="btn btn-outline w-full">
              Estadísticas
            </Link>
            <Link to="/questionnaires" className="btn btn-outline w-full">
              Cuestionarios
            </Link>
          </div>
          <p className="mt-3 text-sm opacity-70">
            Sugerencias rápidas para moverte entre módulos.
          </p>
        </div>
      </section>
    </AppShell>
  );
}
